import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Button, Badge } from '@atoms/index';
import {
  complianceService,
  RESTAURANT_DOCUMENT_TYPES,
  type RestaurantApplicationFormData,
  type StakeholderDocumentItem,
  type LegalStatusResponse,
} from '@services/complianceService';
import {
  RestaurantApplicationFormFields,
  validateSingleApplicationFormField,
} from '@organisms/RestaurantApplicationFormFields';
import {
  isApplicationFormValid,
  mergeRestaurantPrefill,
  validateApplicationForm,
  type ApplicationFormFieldErrors,
  type ApplicationFormFieldKey,
} from '@utils/applicationFormValidation';

async function extractBlobErrorMessage(error: unknown, fallback: string): Promise<string> {
  const response = (error as { response?: { data?: unknown } })?.response;
  if (response?.data instanceof Blob) {
    try {
      const text = await response.data.text();
      const parsed = JSON.parse(text) as { error?: { message?: string }; message?: string };
      return parsed.error?.message ?? parsed.message ?? fallback;
    } catch {
      return fallback;
    }
  }
  const envelope = response?.data as { error?: { message?: string }; message?: string } | undefined;
  return envelope?.error?.message ?? envelope?.message ?? fallback;
}

const DocumentsTab = () => {
  const [documents, setDocuments] = useState<StakeholderDocumentItem[]>([]);
  const [legalStatus, setLegalStatus] = useState<LegalStatusResponse | null>(null);
  const [formData, setFormData] = useState<RestaurantApplicationFormData | null>(null);
  const [prefillData, setPrefillData] = useState<RestaurantApplicationFormData | null>(null);
  const [fieldErrors, setFieldErrors] = useState<ApplicationFormFieldErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isUploadingContract, setIsUploadingContract] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const contractInputRef = useRef<HTMLInputElement>(null);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [docs, legal, form] = await Promise.all([
        complianceService.listMyDocuments(),
        complianceService.getLegalStatus(),
        complianceService.getMyApplicationForm(),
      ]);
      setDocuments(docs);
      setLegalStatus(legal);
      setFormData(form.formData);
      setPrefillData(form.prefill ?? form.formData);
      setFieldErrors({});
    } catch {
      setError('Belgeler yüklenemedi');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const isFormValid = useMemo(
    () => (formData ? isApplicationFormValid(formData) : false),
    [formData],
  );

  const handleFieldBlur = (key: ApplicationFormFieldKey) => {
    if (!formData) return;
    const message = validateSingleApplicationFormField(key, formData[key] ?? '');
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
  };

  const handleApplyRestaurantData = () => {
    if (!formData || !prefillData) return;
    setFormData(mergeRestaurantPrefill(formData, prefillData, true));
    setFieldErrors({});
    setSuccess('Restoran kaydındaki bilgiler forma uygulandı.');
    setError(null);
  };

  const handleDownload = async (documentId: string) => {
    try {
      const url = await complianceService.getMyDocumentDownloadUrl(documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('Belge indirilemedi');
    }
  };

  const handleSaveForm = async () => {
    if (!formData) return;
    const errors = validateApplicationForm(formData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Lütfen işaretli alanları düzeltin.');
      return;
    }

    setIsSavingForm(true);
    setError(null);
    setSuccess(null);
    try {
      await complianceService.saveMyApplicationForm(formData);
      setSuccess('Başvuru formu taslağı kaydedildi');
    } catch {
      setError('Form kaydedilemedi');
    } finally {
      setIsSavingForm(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!formData) return;
    const errors = validateApplicationForm(formData);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('PDF oluşturmak için tüm zorunlu alanları doğru şekilde doldurun.');
      return;
    }

    setIsGeneratingPdf(true);
    setError(null);
    try {
      await complianceService.saveMyApplicationForm(formData);
      const blob = await complianceService.generateMyApplicationFormPdf();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'lezzet-yolda-restoran-basvuru-formu.pdf';
      link.click();
      URL.revokeObjectURL(url);
      setSuccess(
        'PDF indirildi. İmzalı ve kaşeli halini aşağıdan sözleşme olarak yükleyebilirsiniz.',
      );
    } catch (err) {
      setError(await extractBlobErrorMessage(err, 'PDF oluşturulamadı'));
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleUploadContract = async (file: File) => {
    setIsUploadingContract(true);
    setError(null);
    setSuccess(null);
    try {
      await complianceService.uploadMyDocument('SOZLESME', file);
      await loadAll();
      setSuccess('İmzalı sözleşme yüklendi');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || 'Sözleşme yüklenemedi';
      setError(message);
    } finally {
      setIsUploadingContract(false);
    }
  };

  const contractDocument = documents.find((doc) => doc.documentType === 'SOZLESME');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-gray-500">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-xl bg-red-50 text-red-700 text-sm px-4 py-3">{error}</div>
      )}
      {success && (
        <div className="rounded-xl bg-green-50 text-green-700 text-sm px-4 py-3">{success}</div>
      )}

      <Card className="p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">KVKK / Mesafeli Satış Onayı</h2>
        <p className="text-sm text-gray-500 mb-4">
          Platform yasal doküman onay durumunuz
        </p>
        {legalStatus?.document ? (
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={legalStatus.requiresAcceptance ? 'warning' : 'success'}>
              {legalStatus.requiresAcceptance ? 'Onay Bekliyor' : 'Onaylandı'}
            </Badge>
            <span className="text-sm text-gray-700">
              {legalStatus.document.title} (v{legalStatus.document.version})
            </span>
            {legalStatus.acceptance && (
              <span className="text-xs text-gray-500">
                {new Date(legalStatus.acceptance.acceptedAt).toLocaleString('tr-TR')}
              </span>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Henüz yayınlanmış bir platform dokümanı yok.</p>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Resmi Belgelerim</h2>
        <p className="text-sm text-gray-500 mb-4">
          Admin tarafından yüklenen belgelerinizi görüntüleyip indirebilirsiniz.
        </p>
        <div className="space-y-3">
          {RESTAURANT_DOCUMENT_TYPES.map((typeDef) => {
            const doc = documents.find((d) => d.documentType === typeDef.value);
            return (
              <div
                key={typeDef.value}
                className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 p-4"
              >
                <div>
                  <p className="font-medium text-gray-900">{typeDef.label}</p>
                  {doc ? (
                    <p className="text-xs text-gray-500 mt-1">{doc.originalFilename}</p>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1">Henüz yüklenmedi</p>
                  )}
                </div>
                {doc && (
                  <Button size="sm" variant="outline" onClick={() => handleDownload(doc.id)}>
                    İndir
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Başvuru Formu</h2>
        <p className="text-sm text-gray-500 mb-1">
          Formu doldurun, PDF indirin, imzalı/kaselı halini sözleşme olarak yükleyin.
        </p>
        <p className="text-xs text-gray-400 mb-4">
          Boş alanlar restoran kaydından otomatik doldurulur. Kayıtlı bilgileri yeniden uygulamak
          için aşağıdaki butonu kullanın.
        </p>

        {formData && (
          <>
            <div className="mb-4">
              <Button variant="outline" size="sm" onClick={handleApplyRestaurantData}>
                Restoran Kaydından Doldur
              </Button>
            </div>
            <div className="mb-6">
              <RestaurantApplicationFormFields
                formData={formData}
                fieldErrors={fieldErrors}
                onChange={setFormData}
                onFieldBlur={handleFieldBlur}
              />
            </div>
          </>
        )}

        <div className="flex flex-wrap gap-3 mb-6">
          <Button variant="outline" onClick={handleSaveForm} isLoading={isSavingForm}>
            Taslak Kaydet
          </Button>
          <Button
            onClick={handleGeneratePdf}
            isLoading={isGeneratingPdf}
            disabled={!isFormValid}
            title={!isFormValid ? 'PDF için tüm alanları doğru doldurun' : undefined}
          >
            PDF İndir
          </Button>
        </div>

        <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-4">
          <h3 className="font-semibold text-gray-900 mb-2">İmzalı Sözleşme Yükle</h3>
          <p className="text-sm text-gray-600 mb-4">
            PDF formunu imzalayıp kaşeledikten sonra buradan yükleyin.
          </p>
          {contractDocument && (
            <p className="text-xs text-green-700 mb-3">
              Mevcut: {contractDocument.originalFilename} (
              {new Date(contractDocument.updatedAt).toLocaleDateString('tr-TR')})
            </p>
          )}
          <input
            ref={contractInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleUploadContract(file);
              e.target.value = '';
            }}
          />
          <Button
            variant="outline"
            isLoading={isUploadingContract}
            onClick={() => contractInputRef.current?.click()}
          >
            {contractDocument ? 'Sözleşmeyi Güncelle' : 'İmzalı Sözleşme Yükle'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default DocumentsTab;
